import { motion } from "framer-motion";
import { Search, UserCheck, Phone, FileText, Bell, TrendingUp } from "lucide-react";
import { useInView } from "@/hooks";
import { howItWorksService } from "@/services/firebase";
import { useState, useEffect } from "react";
import type { HowItWorksSection } from "@/types";

const iconMap: Record<string, React.ElementType> = {
  search: Search,
  "user-check": UserCheck,
  phone: Phone,
  "file-text": FileText,
  bell: Bell,
  "trending-up": TrendingUp,
};

export function HowItWorksSection() {
  const { ref, isInView } = useInView();
  const [data, setData] = useState<HowItWorksSection | null>(null);

  useEffect(() => {
    howItWorksService.getAll().then((items) => setData(items[0] || null));
  }, []);

  return (
    <section id="how-it-works" ref={ref} className="py-20 bg-light">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-dark mb-4">
            {data?.title || "Simple. Rapide. Efficace."}
          </h2>
          <p className="text-lg text-gray-500">
            {data?.subtitle || "Allo Artisan en 3 étapes, que vous soyez client ou artisan."}
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Client Side */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8 shadow-card"
          >
            <h3 className="text-xl font-bold text-primary mb-8 flex items-center gap-2">
              <span className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <UserCheck className="w-4 h-4 text-primary" />
              </span>
              Côté Client
            </h3>
            <div className="space-y-8">
              {(data?.clientSteps || []).map((step, index) => {
                const Icon = iconMap[step.icon] || Search;
                return (
                  <div key={step.id} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-primary text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {step.number}
                    </div>
                    <div>
                      <h4 className="font-bold text-dark mb-1 flex items-center gap-2">
                        <Icon className="w-4 h-4 text-primary" />
                        {step.title}
                      </h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>

          {/* Artisan Side */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-8 shadow-card"
          >
            <h3 className="text-xl font-bold text-secondary mb-8 flex items-center gap-2">
              <span className="w-8 h-8 bg-secondary/10 rounded-lg flex items-center justify-center">
                <FileText className="w-4 h-4 text-secondary" />
              </span>
              Côté Artisan
            </h3>
            <div className="space-y-8">
              {(data?.artisanSteps || []).map((step) => {
                const Icon = iconMap[step.icon] || FileText;
                return (
                  <div key={step.id} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 bg-secondary text-white rounded-full flex items-center justify-center font-bold text-sm">
                      {step.number}
                    </div>
                    <div>
                      <h4 className="font-bold text-dark mb-1 flex items-center gap-2">
                        <Icon className="w-4 h-4 text-secondary" />
                        {step.title}
                      </h4>
                      <p className="text-gray-500 text-sm leading-relaxed">{step.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
